export type RequestFunction<T> = () => Promise<T>;

export type QueueStatus =
  | 'IDLE'
  | 'PROCESSING'
  | 'SUCCESS'
  | 'FAILURE'
  | 'TERMINATED';

export interface QueueState {
  totalCount: number;
  completedCount: number;
  status: QueueStatus;
  elapsedTime: number;
  error?: Error;
}

/**
 * Encapsulates all queue processing logic including concurrency control,
 * result collection, elapsed time tracking, and termination handling.
 *
 * Use like this:
 *
 * const queue = new RequestQueue({
 *   maxConcurrent: 10,
 *   onStateChange: (queueState) => {
 *     console.log(queueState);
 *   },
 * });
 *
 * for (const request of manyRequests) {
 *   queue.push(request);
 * }
 *
 * const results = await queue.getResults();
 */
export class RequestQueue<T> {
  private queue: RequestFunction<T>[] = [];

  private totalCount = 0;

  private inFlightCount = 0;

  private completedCount = 0;

  private results: T[] = [];

  private status: QueueStatus = 'IDLE';

  private error?: Error;

  private elapsedTime = 0;

  private startTime = 0;

  private elapsedTimeInterval: NodeJS.Timeout | null = null;

  private resolveHandler: ((value: T[]) => void) | null = null;

  private rejectHandler: ((error: Error) => void) | null = null;

  private onStateChange: ((state: QueueState) => void) | null = null;

  private maxConcurrent: number;

  constructor({
    maxConcurrent,
    onStateChange,
    abortSignal,
  }: {
    maxConcurrent: number;
    onStateChange: (state: QueueState) => void;
    abortSignal?: AbortSignal;
  }) {
    this.maxConcurrent = maxConcurrent;
    this.onStateChange = onStateChange;

    // Set up automatic termination on abort
    abortSignal?.addEventListener('abort', () => {
      this.terminate();
    });
  }

  /**
   * Starts tracking elapsed time for the current queue processing session.
   * Sets up a 1-second interval to update the elapsed time.
   */
  private startElapsedTimeTracking(): void {
    this.startTime = Date.now();

    if (this.elapsedTimeInterval) {
      clearInterval(this.elapsedTimeInterval);
    }

    this.elapsedTimeInterval = setInterval(() => {
      if (this.status !== 'PROCESSING') {
        this.stopElapsedTimeTracking();
        return;
      }

      this.elapsedTime = Math.round((Date.now() - this.startTime) / 1000);
      this.notifyStateChange();
    }, 1000);
  }

  /**
   * Stops the elapsed time tracking by clearing the interval and setting the final elapsed time.
   */
  private stopElapsedTimeTracking(): void {
    if (this.elapsedTimeInterval) {
      clearInterval(this.elapsedTimeInterval);
    }

    this.elapsedTime = Math.round((Date.now() - this.startTime) / 1000);
    this.notifyStateChange();
  }

  /**
   * Notifies the React hook of state changes by calling the onStateChange callback.
   */
  private notifyStateChange(): void {
    if (!this.onStateChange) return;

    this.onStateChange({
      totalCount: this.totalCount,
      completedCount: this.completedCount,
      status: this.status,
      elapsedTime: this.elapsedTime,
      error: this.error,
    });
  }

  /**
   * Processes the next request in the queue with controlled concurrency.
   * Handles request execution, result collection, error handling, and recursive processing.
   */
  private async processNext(): Promise<void> {
    if (this.shouldStopProcessing()) return;

    const nextRequest = this.queue.shift();

    if (!nextRequest) {
      if (this.inFlightCount === 0) {
        this.stopElapsedTimeTracking();
        this.status = 'SUCCESS';
        this.notifyStateChange();

        if (this.resolveHandler) {
          this.resolveHandler([...this.results]);
        }
      }
      return;
    }

    this.inFlightCount++;

    try {
      const response = await nextRequest();

      // Check if terminated during the async operation
      if (this.isTerminated()) {
        this.inFlightCount--;
        return;
      }

      this.results.push(response);
      this.completedCount++;
      this.inFlightCount--;
      this.notifyStateChange();

      // Continue processing
      this.processNext();
    } catch (error) {
      this.inFlightCount--;

      // Check if terminated during the async operation
      if (!this.isTerminated()) {
        this.stopElapsedTimeTracking();
        this.status = 'FAILURE';
        this.error = error as Error;
        this.notifyStateChange();

        if (this.rejectHandler) {
          this.rejectHandler(error as Error);
        }
      }
    }
  }

  /**
   * Adds a new request function to the queue and starts processing if capacity allows.
   *
   * @param request - A function that returns a Promise<T> to be executed
   */
  push(request: RequestFunction<T>): void {
    if (this.status === 'TERMINATED') return;

    const isFirstRequest = this.queue.length === 0 && this.inFlightCount === 0;

    this.queue.push(request);
    this.totalCount++;
    this.status = 'PROCESSING';
    this.notifyStateChange();

    // Start elapsed time tracking on first request
    if (isFirstRequest) {
      this.startElapsedTimeTracking();
    }

    // Start processing if we have capacity
    if (this.inFlightCount < this.maxConcurrent) {
      this.processNext();
    }
  }

  /**
   * Returns a Promise that resolves with all completed results when processing finishes.
   * Resolves immediately if no requests are queued or in-flight.
   *
   * @returns Promise<T[]> - Array of all completed request results
   */
  getResults(): Promise<T[]> {
    if (this.status === 'TERMINATED') {
      return Promise.resolve([...this.results]);
    }

    return new Promise((resolve, reject) => {
      // If no requests are queued or in flight, resolve immediately
      if (this.queue.length === 0 && this.inFlightCount === 0) {
        resolve([...this.results]);
        return;
      }

      // Store the resolve/reject functions to be called when the queue is finished (or errors)
      this.resolveHandler = resolve;
      this.rejectHandler = reject;
    });
  }

  /**
   * Terminates the queue by setting the status to TERMINATED and cleaning up resources.
   * Any in-flight requests will be ignored when they complete.
   */
  terminate(): void {
    this.status = 'TERMINATED';

    if (this.elapsedTimeInterval) {
      clearInterval(this.elapsedTimeInterval);
    }

    // Notify about termination before cleaning up the callback
    this.notifyStateChange();
  }

  /**
   * Checks if the queue should stop processing.
   *
   * @returns true if the queue should stop processing, false otherwise
   */
  shouldStopProcessing(): boolean {
    return this.status === 'TERMINATED' || this.status === 'FAILURE';
  }

  /**
   * Gets the current state of the queue.
   */
  getState(): QueueState {
    return {
      totalCount: this.totalCount,
      completedCount: this.completedCount,
      status: this.status,
      elapsedTime: this.elapsedTime,
    };
  }

  /**
   * Checks if the queue has been terminated.
   */
  isTerminated(): boolean {
    return this.status === 'TERMINATED';
  }

  /**
   * Gets the current status of the queue.
   */
  getStatus(): QueueStatus {
    return this.status;
  }
}
